import { join, relative } from "node:path";
// eslint-disable-next-line import/no-extraneous-dependencies
import { AWS_STAGE_TYPE, AwsStageType } from "@codedrifters/utils";
import { Component } from "projen";
import { AwsCdkTypeScriptApp } from "projen/lib/awscdk";
import { AwsDeploymentTarget } from "./aws-deployment-target";
import { CdkCli } from "./cdk-cli";
import { mergeCdkOptions } from "./cdk-cli-merge";
import { CdkSynthOptions } from "./cdk-cli-options";
import { renderCdkSynth } from "./cdk-cli-renderer";
import { GIT_BRANCH_NAME_ENV, GIT_BRANCH_NAME_ENV_VALUE } from "../git";
import { TurboRepo } from "../turbo";

/**
 * Sort weight per stage, lowest precedence first. Used to fold per-target synth
 * options in a stable, declaration-order-independent way.
 */
const SYNTH_STAGE_RANK: Record<AwsStageType, number> = {
  [AWS_STAGE_TYPE.DEV]: 0,
  [AWS_STAGE_TYPE.STAGE]: 1,
  [AWS_STAGE_TYPE.PROD]: 2,
};

/*******************************************************************************
 *
 * AWS Deployment Configuration
 *
 * This component allows configuration of multiple AWS deployment
 * targets, each with its own account, region, and deployment type
 * (dev, stage, prod). It supports both local and CI deployments,
 * with customizable settings for each target.
 *
 ******************************************************************************/

/*
export interface AwsDeploymentConfigOptions {}
*/

export class AwsDeploymentConfig extends Component {
  public static of(
    project: AwsCdkTypeScriptApp,
  ): AwsDeploymentConfig | undefined {
    const isDefined = (c: Component): c is AwsDeploymentConfig =>
      c instanceof AwsDeploymentConfig;
    return project.components.find(isDefined);
  }

  /**
   * Environment variables to be injected into all tasks.
   */
  public readonly env: Record<string, string>;

  /**
   * The relative path to the project directory from the root of the project.
   */
  public readonly projectPath: string;

  /**
   * The relative path to the root of the project from the output directory.
   */
  public readonly rootPath: string;

  /**
   * The output directory for the CDK synthesis, from the root directory.
   */
  public readonly rootCdkOut: string;

  /**
   * The output directory for the CDK synthesis.
   */
  public readonly cdkOut: string;

  /**
   * Array of targets for deployment.
   */
  public readonly awsDeploymentTargets: Array<AwsDeploymentTarget> = [];

  /**
   * The CDK CLI component that owns shared `cdk` invocation options for this
   * project.
   */
  public readonly cdkCli: CdkCli;

  constructor(project: AwsCdkTypeScriptApp) {
    super(project);

    /**
     * Ensure the CdkCli component is attached so deploy / watch / synth tasks
     * can resolve typed options instead of hard-coding CLI strings.
     */
    this.cdkCli = CdkCli.of(project) || new CdkCli(project);

    /**
     * Common variables used across tasks.
     *
     * This is the BELOW-turbo layer: it resolves the branch actually baked
     * into the synthesized assembly. Its `[ -n "$GIT_BRANCH_NAME" ]` guard
     * consumes whatever the ABOVE-turbo layer (the root `build:all` task env,
     * written by `TurboRepo.activateBranchNameEnvVar`) exported, so both share
     * one constant and cannot drift — see `git-branch-env.ts`.
     */
    this.env = {
      [GIT_BRANCH_NAME_ENV]: GIT_BRANCH_NAME_ENV_VALUE,
    };
    this.projectPath = relative(project.root.outdir, project.outdir);
    this.rootPath = relative(project.outdir, project.root.outdir);
    this.rootCdkOut = join("dist", this.projectPath, "cdk.out");
    this.cdkOut = join(this.rootPath, "dist", this.projectPath, "cdk.out");

    /**
     * Reset some tasks we will rebuild below.
     */
    ["deploy", "watch"].forEach((taskName) => {
      const task = project.tasks.tryFind(taskName);
      if (task) {
        task.reset();
        task.say(
          "Generic task is disabled. Please use the specific task for your deployment target.",
        );
      }
    });

    /**
     * Redefine Synth here
     *
     * Deploy and watch get reconfigured per deployment target.
     * @see addDeploymentTarget()
     */
    this.configureSynthTask();
  }

  /*****************************************************************************
   *
   * Target filter helpers
   *
   * Return various targets for deployment scripts to use.
   *
   ****************************************************************************/

  /**
   * @returns All production deployment targets.
   */
  public get prodTargets(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) => target.awsStageType === AWS_STAGE_TYPE.PROD,
    );
  }
  public get prodTargetsForCI(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.PROD && target.ciDeployment,
    );
  }
  public get prodTargetsForLocal(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.PROD && target.localDeployment,
    );
  }

  /**
   *
   * @returns All stage deployment targets.
   */
  public get stageTargets(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) => target.awsStageType === AWS_STAGE_TYPE.STAGE,
    );
  }
  public get stageTargetsForCI(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.STAGE && target.ciDeployment,
    );
  }
  public get stageTargetsForLocal(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.STAGE && target.localDeployment,
    );
  }

  /**
   *
   * @returns All dev deployment targets.
   */
  public get devTargets(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) => target.awsStageType === AWS_STAGE_TYPE.DEV,
    );
  }
  public get devTargetsForCI(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.DEV && target.ciDeployment,
    );
  }
  public get devTargetsForLocal(): Array<AwsDeploymentTarget> {
    return this.awsDeploymentTargets.filter(
      (target) =>
        target.awsStageType === AWS_STAGE_TYPE.DEV && target.localDeployment,
    );
  }

  /*****************************************************************************
   *
   * Synth Tasks
   *
   * - Configure synth task to use the branch name
   * - Change the output location for easier workflows.
   *
   ****************************************************************************/

  /**
   * Resolve the synth options for the project-wide `synth` / `synth:silent`
   * tasks.
   *
   * `cdk synth` produces one cloud assembly that every target deploys from, so
   * there is no single target to resolve against. Each registered target's
   * resolved options are folded together lowest-stage-first, so a higher stage
   * wins scalar collisions and arrays/records union across targets. Sorting
   * makes the result independent of the order targets were declared in. With a
   * single target this is exactly `synthOptionsFor(target)`; with none it is
   * empty.
   */
  private resolveSynthOptions = (): CdkSynthOptions => {
    const ordered = [...this.awsDeploymentTargets].sort(
      (a, b) =>
        SYNTH_STAGE_RANK[a.awsStageType] - SYNTH_STAGE_RANK[b.awsStageType] ||
        a.account.localeCompare(b.account) ||
        a.region.localeCompare(b.region),
    );

    return mergeCdkOptions<CdkSynthOptions>(
      ...ordered.map((target) => this.cdkCli.synthOptionsFor(target)),
    );
  };

  /**
   * (Re)build the project-wide synth tasks.
   *
   * Idempotent — it resets both tasks before rebuilding them, so it is safe to
   * run once from the constructor (keeping the tasks well-formed for a project
   * with no targets) and again from `preSynthesize`, once every target has
   * registered itself and the override chain can actually be resolved.
   *
   * `--output` is pinned at the call site because it is a property of the
   * project layout, and `--quiet` is pinned on `synth:silent` because it is
   * what makes that task silent. Every other flag flows through the chain.
   */
  private configureSynthTask = (): void => {
    const synthOpts = this.resolveSynthOptions();

    this.project.tasks.tryFind("synth")?.reset(`rm -rf ${this.cdkOut}`);
    this.project.tasks
      .tryFind("synth")
      ?.exec(`cdk ${renderCdkSynth({ ...synthOpts, output: this.cdkOut })}`, {
        env: this.env,
      });

    this.project.tasks.tryFind("synth:silent")?.reset(`rm -rf ${this.cdkOut}`);
    this.project.tasks.tryFind("synth:silent")?.exec(
      `cdk ${renderCdkSynth({
        ...synthOpts,
        output: this.cdkOut,
        quiet: true,
      })}`,
      {
        env: this.env,
      },
    );
  };

  preSynthesize(): void {
    super.preSynthesize();

    /**
     * Rebuild the synth tasks now that every deployment target has registered
     * itself. Targets attach during construction, which happens after this
     * component's own constructor ran, so this is the first point at which the
     * per-target synth override chain can be resolved.
     */
    this.configureSynthTask();

    /**
     * If turbo's active we should ensure the post compile task
     * is configured to consider the cdk output directory in it's root location.
     */
    if (TurboRepo.of(this.project)) {
      const turbo = TurboRepo.of(this.project)!;
      turbo.postCompileTask?.outputs.push(join(this.cdkOut, "**"));
    }
  }
}
